// AIFinder.jsx - Con bottone Meteo
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import WeatherAlerts from './WeatherAlerts';

function ExpandableSummary({ summary }) {
    const [expanded, setExpanded] = useState(false);
    const MAX_LENGTH = 100;

    const isLong = summary && summary.length > MAX_LENGTH;
    const displayText = expanded || !isLong
        ? summary
        : summary.slice(0, MAX_LENGTH) + '...';

    return (
        <span
            onClick={() => isLong && setExpanded(!expanded)}
            style={{
                background: '#f0f0f0',
                color: '#333',
                padding: '6px 12px',
                borderRadius: '16px',
                fontSize: '12px',
                fontWeight: '500',
                display: 'inline-block',
                cursor: isLong ? 'pointer' : 'default',
                maxWidth: '100%',
                wordWrap: 'break-word',
                transition: 'all 0.2s ease',
                position: 'relative',
                ...(isLong && { paddingRight: '28px' })
            }}
        >
            💬 {displayText}
            {isLong && (
                <span style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '10px',
                    opacity: 0.6
                }}>
                    {expanded ? '▲' : '▼'}
                </span>
            )}
        </span>
    );
}

function getRouteProperties(route) {
    return route.properties || route || {};
}

function getFirstCoordinate(feature) {
    if (!feature) return null;
    const p = feature.properties || {};
    if (p.lat && p.lon) return { lat: parseFloat(p.lat), lng: parseFloat(p.lon) };
    if (feature.geometry?.coordinates) {
        const coords = feature.geometry.coordinates;
        if (feature.geometry.type === 'LineString') {
            const [lng, lat] = coords[0];
            return { lat, lng };
        } else if (feature.geometry.type === 'MultiLineString') {
            const [lng, lat] = coords[0][0];
            return { lat, lng };
        }
    }
    return null;
}

export default function AIFinder({
    databaseReady = false,
    filteredFeatures = [],
    allFeatures = [],
    summaries = null,
    currentRegion = ''
}) {
    const { i18n, t } = useTranslation();
    const [searchMode, setSearchMode] = useState('filtered');
    const [queryText, setQueryText] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [aiMessage, setAiMessage] = useState('');
    const [inputWasTranslated, setInputWasTranslated] = useState(false);
    const [error, setError] = useState('');
    const [aiResults, setAiResults] = useState([]);
    const [weatherEnabled, setWeatherEnabled] = useState(false);
    const [weatherDate, setWeatherDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [noWeatherMatch, setNoWeatherMatch] = useState(false);
    const [showWeatherModal, setShowWeatherModal] = useState(false);
    const [selectedTrailForWeather, setSelectedTrailForWeather] = useState(null);

    const displayResults = searchMode === 'filtered' ? filteredFeatures : aiResults;

    useEffect(() => {
        if (searchMode === 'filtered') {
            setAiResults([]);
            setAiMessage('');
            setError('');
        }
    }, [searchMode]);

    const handleShowWeather = (route) => {
        const fullRoute = searchMode === 'filtered'
            ? route
            : getFullRoute(route.id || getRouteProperties(route).id, getRouteProperties(route).name);

        if (!fullRoute) {
            alert(t('route_data_unavailable'));
            return;
        }
        setSelectedTrailForWeather(fullRoute);
        setShowWeatherModal(true);
    };

    const getFullRoute = (routeId, routeName) => {
        const searchIn = allFeatures.length > 0 ? allFeatures : filteredFeatures;
        if (!searchIn || searchIn.length === 0) {
            console.warn('Nessun percorso disponibile per la ricerca');
            return null;
        }
        const fullRoute = searchIn.find(f =>
            f.id === routeId ||
            f.properties?.id === routeId ||
            f.properties?.name === routeName ||
            f.properties?.name === routeId
        );
        if (fullRoute) {
            console.log('✅ Percorso completo trovato:', fullRoute.properties?.name);
        } else {
            console.warn('❌ Percorso completo non trovato per:', routeId, routeName);
        }
        return fullRoute;
    };

    // ==========================================
    // RICERCA AI
    // ==========================================
    // ==========================================
    // RICERCA AI
    // ==========================================
    const handleSearch = async () => {
        if (!queryText.trim()) return;
        if (!databaseReady) {
            setError(t('not_ready'));
            return;
        }
        setIsSearching(true);
        setError('');
        setAiMessage('');
        setNoWeatherMatch(false);
        setInputWasTranslated(false);

        let textToSend = queryText;
        let wasTranslated = false; // variabile locale, non dipende da setState

        // Se lingua attiva è inglese, traduci la QUERY in italiano prima di inviare
        if (i18n.language === 'en') {
            try {
                const res = await fetch(`https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=it&dt=t&q=${encodeURIComponent(queryText)}`);
                const data = await res.json();
                if (Array.isArray(data) && data[0]?.[0]?.[0]) {
                    textToSend = data[0].map((part) => part[0]).join('');
                    wasTranslated = true;          // ← locale, disponibile subito
                    setInputWasTranslated(true);   // ← solo per il badge UI
                }
            } catch {
                textToSend = queryText;
            }
        }

        try {
            const body = {
                messages: [{ role: 'user', content: textToSend }],
                limit: 10,
                ...(weatherEnabled ? { date: weatherDate } : {})
            };
            const response = await fetch(`${process.env.REACT_APP_API_URL_NODE}/api/chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            if (!response.ok) {
                const payload = await response.json().catch(() => null);
                const msg = payload?.error
                    ? `${payload.error}${payload.hint ? ` — ${payload.hint}` : ''}`
                    : `HTTP ${response.status}`;
                throw new Error(msg);
            }
            const data = await response.json();

            if (data.noWeatherMatch) {
                setNoWeatherMatch(true);
                setAiResults([]);
                setAiMessage('');
            } else if (data.candidates && data.candidates.length > 0) {
                setAiResults(data.candidates);

                // ✅ FIX: usa wasTranslated (locale) invece di inputWasTranslated (state asincrono)
                let message = data.message || t('ai_results', { count: data.candidates.length });
                if (wasTranslated && data.message) {
                    try {
                        const res = await fetch(`https://translate.googleapis.com/translate_a/single?client=gtx&sl=it&tl=en&dt=t&q=${encodeURIComponent(data.message)}`);
                        const translated = await res.json();
                        if (Array.isArray(translated) && translated[0]?.[0]?.[0]) {
                            message = translated[0].map((part) => part[0]).join('');
                        }
                    } catch {
                        // fallback: usa il messaggio originale italiano
                    }
                }
                setAiMessage(message);
                console.log(`✅ Ricevuti ${data.candidates.length} candidati dall'AI (senza filtri)`);
            } else {
                setAiMessage(t('no_results'));
                setAiResults([]);
            }
        } catch (err) {
            console.error('❌ Errore ricerca AI:', err);
            setError(`${t('error_prefix')}: ${err.message}`);
        } finally {
            setIsSearching(false);
        }
    };
    // ==========================================
    // RENDER SINGOLO PERCORSO
    // ==========================================
    const renderRoute = (route, index) => {
        const p = getRouteProperties(route);
        const isRecommended = searchMode === 'ai' && index === 0;

        const fullRoute = searchMode === 'filtered'
            ? route
            : getFullRoute(route.id || p.id, p.name);

        const firstCoord = fullRoute ? getFirstCoordinate(fullRoute) : null;
        const googleMapsUrl = firstCoord
            ? `https://www.google.com/maps/dir/?api=1&destination=${firstCoord.lat},${firstCoord.lng}&travelmode=driving`
            : null;

        return (
            <div
                key={route.id || index}
                style={{
                    background: isRecommended ? 'linear-gradient(to bottom, #f1f8f4 0%, white 100%)' : 'white',
                    border: isRecommended ? '2px solid #4CAF50' : '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '16px',
                    transition: 'all 0.2s ease'
                }}
            >
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px', gap: '12px' }}>
                    <div title={p.description || ''} style={{ flex: 1, minWidth: 0 }}>
                        {isRecommended && (
                            <div style={{
                                display: 'inline-block',
                                background: '#4CAF50',
                                color: 'white',
                                fontSize: '11px',
                                fontWeight: '600',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                marginBottom: '6px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                ⭐ {t('recommended')}
                            </div>
                        )}
                        <h4 style={{
                            margin: 0,
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#2196F3',
                            wordWrap: 'break-word'
                        }}>
                            {p.name || t('unnamed_route')}
                        </h4>
                    </div>
                    {searchMode === 'ai' && route.score && (
                        <div style={{
                            fontSize: '12px',
                            color: '#999',
                            background: '#f5f5f5',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            whiteSpace: 'nowrap'
                        }}>
                            Score: {route.score.toFixed(3)}
                        </div>
                    )}
                </div>

                {/* Località */}
                {(p.from || p.to) && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px', fontSize: '14px' }}>
                        {p.from && (
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <span style={{ color: '#666', fontWeight: '500' }}>📍 {t('from')}:</span>
                                <span style={{ color: '#333' }}>{p.from}</span>
                            </div>
                        )}
                        {p.to && (
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <span style={{ color: '#666', fontWeight: '500' }}>🎯 {t('to')}:</span>
                                <span style={{ color: '#333' }}>{p.to}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Caratteristiche */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                    {summaries[route.id] && (
                        <ExpandableSummary summary={summaries[route.id]} />
                    )}
                    {p.lengthCategory && p.lengthCategory !== '__NONE__' && (
                        <span style={{
                            background: '#f0f0f0', color: '#333',
                            padding: '6px 12px', borderRadius: '16px',
                            fontSize: '13px', fontWeight: '500', whiteSpace: 'nowrap'
                        }}>
                            📏 {p.lengthCategory}
                        </span>
                    )}
                    {p.ascentCategory && p.ascentCategory !== '__NONE__' && (
                        <span style={{
                            background: '#f0f0f0', color: '#333',
                            padding: '6px 12px', borderRadius: '16px',
                            fontSize: '13px', fontWeight: '500', whiteSpace: 'nowrap'
                        }}>
                            ⬆️ {p.ascentCategory}
                        </span>
                    )}
                    {p.totalDurationCategory && p.totalDurationCategory !== '__NONE__' && (
                        <span style={{
                            background: '#f0f0f0', color: '#333',
                            padding: '6px 12px', borderRadius: '16px',
                            fontSize: '13px', fontWeight: '500', whiteSpace: 'nowrap'
                        }}>
                            ⏱️ {p.totalDurationCategory}
                        </span>
                    )}
                    {p.cai_scale && p.cai_scale !== '_NONE__' && (
                        <span style={{
                            background: '#FFF3E0', color: '#F57C00',
                            padding: '6px 12px', borderRadius: '16px',
                            fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap'
                        }}>
                            🎯 {p.cai_scale}
                        </span>
                    )}
                    {p.weather && (
                        <span style={{
                            background: '#E3F2FD', color: '#0D47A1',
                            padding: '6px 12px', borderRadius: '16px',
                            fontSize: '13px', fontWeight: '500', whiteSpace: 'nowrap'
                        }}>
                            {p.weather.emoji} {p.weather.label} | 💧{p.weather.precipitation}mm | 💨{p.weather.windSpeed}km/h | 🌡️{p.weather.tempMax}°C max
                        </span>
                    )}
                </div>

                {/* PULSANTI NAVIGAZIONE + METEO */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {googleMapsUrl ? (
                        <a href={googleMapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                padding: '8px 16px', background: '#4285F4', color: 'white',
                                textDecoration: 'none', borderRadius: '4px', fontSize: '14px',
                                fontWeight: '500', transition: 'background 0.2s',
                                flex: '1 1 auto', minWidth: '120px'
                            }}
                        >
                            🚗 {t('go_to_start')}
                        </a>
                    ) : (
                        <button disabled style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            padding: '8px 16px', background: '#e0e0e0', color: '#999',
                            border: 'none', borderRadius: '4px', fontSize: '14px',
                            fontWeight: '500', cursor: 'not-allowed',
                            flex: '1 1 auto', minWidth: '120px'
                        }}>
                            🚗 {t('not_available')}
                        </button>
                    )}

                    <button
                        onClick={() => {
                            if (!fullRoute || !fullRoute.geometry) {
                                alert(t('track_unavailable'));
                                return;
                            }
                            if (window.viewRouteDetail) {
                                window.viewRouteDetail(fullRoute);
                            }
                        }}
                        disabled={!fullRoute || !fullRoute.geometry}
                        style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            padding: '8px 16px',
                            background: fullRoute?.geometry ? '#4CAF50' : '#e0e0e0',
                            color: fullRoute?.geometry ? 'white' : '#999',
                            border: 'none', borderRadius: '4px', fontSize: '14px',
                            fontWeight: '500',
                            cursor: fullRoute?.geometry ? 'pointer' : 'not-allowed',
                            flex: '1 1 auto', minWidth: '120px'
                        }}
                    >
                        🗺️ {t('full_route')}
                    </button>

                    <button
                        onClick={() => handleShowWeather(route)}
                        style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            padding: '8px 16px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white', border: 'none', borderRadius: '4px',
                            fontSize: '14px', fontWeight: '500', cursor: 'pointer',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            flex: '1 1 auto', minWidth: '120px'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                        }}
                    >
                        🌤️ {t('weather')}
                    </button>
                </div>
            </div>
        );
    };

    // ==========================================
    // RENDER PRINCIPALE
    // ==========================================
    return (
        <>
            <div
                className="ai-finder-container"
                style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}
            >
                {/* SEZIONE FISSA: Barra di ricerca AI */}
                <div style={{
                    flexShrink: 0, padding: '16px',
                    borderBottom: '1px solid #ddd', backgroundColor: 'white'
                }}>
                    <h3 style={{ margin: '0 0 12px 0' }}>🤖 {t('ai_search_title')}</h3>

                    {!databaseReady && (
                        <div style={{
                            padding: '12px', background: '#fff3cd',
                            border: '1px solid #ffc107', borderRadius: '4px',
                            marginBottom: '12px', fontSize: '14px'
                        }}>
                            ⏳ {t('db_loading')}
                        </div>
                    )}

                    {/* RADIO BUTTON: Dati Filtrati / Cerca con AI */}
                    <div style={{
                        display: 'flex', gap: '16px', marginBottom: '12px',
                        padding: '12px', background: '#f5f5f5', borderRadius: '8px'
                    }}>
                        <label style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            cursor: 'pointer', fontSize: '14px', fontWeight: '500'
                        }}>
                            <input
                                type="radio"
                                name="searchMode"
                                value="filtered"
                                checked={searchMode === 'filtered'}
                                onChange={(e) => setSearchMode(e.target.value)}
                                style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                            />
                            📊 {t('filtered_data')} ({filteredFeatures.length})
                        </label>

                        <label style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            cursor: 'pointer', fontSize: '14px', fontWeight: '500'
                        }}>
                            <input
                                type="radio"
                                name="searchMode"
                                value="ai"
                                checked={searchMode === 'ai'}
                                onChange={(e) => setSearchMode(e.target.value)}
                                style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                            />
                            🔍 {t('search_with_ai')}
                        </label>
                    </div>

                    {/* Barra di ricerca AI */}
                    {searchMode === 'ai' && (
                        <>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <input
                                    type="text"
                                    placeholder={t('search_placeholder')}
                                    value={queryText}
                                    onChange={(e) => setQueryText(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                                    disabled={isSearching || !databaseReady}
                                    style={{
                                        flex: 1, minWidth: '200px', padding: '10px',
                                        border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px'
                                    }}
                                />
                                <button
                                    onClick={handleSearch}
                                    disabled={isSearching || !databaseReady || !queryText.trim()}
                                    style={{
                                        padding: '10px 20px',
                                        background: databaseReady ? '#2196F3' : '#ccc',
                                        color: 'white', border: 'none', borderRadius: '4px',
                                        cursor: databaseReady ? 'pointer' : 'not-allowed',
                                        fontSize: '14px', whiteSpace: 'nowrap', flexShrink: 0
                                    }}
                                >
                                    {isSearching ? `🔄 ${t('searching')}` : `🔍 ${t('search')}`}
                                </button>
                            </div>

                            {/* Filtro meteo */}
                            <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer', userSelect: 'none' }}>
                                    <input
                                        type="checkbox"
                                        checked={weatherEnabled}
                                        onChange={(e) => { setWeatherEnabled(e.target.checked); setNoWeatherMatch(false); }}
                                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                    />
                                    ☁️ {t('consider_weather')}
                                </label>
                                {weatherEnabled && (
                                    <input
                                        type="date"
                                        value={weatherDate}
                                        min={new Date().toISOString().slice(0, 10)}
                                        max={new Date(Date.now() + 16 * 86400000).toISOString().slice(0, 10)}
                                        onChange={(e) => setWeatherDate(e.target.value)}
                                        style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                                    />
                                )}
                            </div>

                            {noWeatherMatch && (
                                <div style={{
                                    marginTop: '8px', padding: '12px',
                                    background: '#fff8e1', border: '1px solid #ffca28',
                                    borderRadius: '4px', fontSize: '14px',
                                    color: '#5d4037', lineHeight: '1.5'
                                }}>
                                    ⛅ <strong>{t('no_weather_match')}</strong><br />
                                    {t('no_weather_match_hint')}
                                </div>
                            )}
                            {error && (
                                <div style={{
                                    marginTop: '8px', padding: '10px',
                                    background: '#ffebee', color: '#c62828',
                                    borderRadius: '4px', fontSize: '14px'
                                }}>
                                    {error}
                                </div>
                            )}
                            {aiMessage && (
                                <div style={{
                                    marginTop: '8px', padding: '10px',
                                    background: '#e3f2fd', color: '#1565c0',
                                    borderRadius: '4px', fontSize: '14px',
                                    maxHeight: '20vh', overflowY: 'auto',
                                    lineHeight: '1.5', WebkitOverflowScrolling: 'touch',
                                    display: 'flex', alignItems: 'center', gap: '10px'
                                }}>
                                    💬 {aiMessage}
                                    {inputWasTranslated && (
                                        <span style={{
                                            background: '#ffd700', color: '#333',
                                            borderRadius: '8px', padding: '2px 8px',
                                            fontSize: '12px', fontWeight: 600,
                                            marginLeft: '8px', display: 'inline-block'
                                        }}>
                                            {t('translated_badge')}
                                        </span>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    {/* Info modalità Dati Filtrati */}
                    {searchMode === 'filtered' && (
                        <div style={{
                            padding: '12px', background: '#e3f2fd',
                            border: '1px solid #2196F3', borderRadius: '4px', fontSize: '14px'
                        }}>
                            📊 {t('filtered_mode_info_pre')} <strong>{filteredFeatures.length}</strong> {t('filtered_mode_info_post')}
                        </div>
                    )}
                </div>

                {/* SEZIONE FISSA: Header con contatori */}
                <div style={{
                    flexShrink: 0, padding: '12px 16px',
                    background: '#f8f9fa', borderBottom: '1px solid #e0e0e0'
                }}>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>
                        {displayResults.length}
                        {' '}
                        {displayResults.length === 1 ? t('track_found_single', 'percorso trovato') : t('tracks_found', 'percorsi trovati')}
                    </div>
                </div>

                {/* SEZIONE ESPANDIBILE: Lista risultati */}
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px' }}>
                    {displayResults.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                                {searchMode === 'filtered' ? '📊' : '🔍'}
                            </div>
                            <h4 style={{ margin: '0 0 8px 0' }}>
                                {searchMode === 'filtered'
                                    ? t('no_filtered_routes')
                                    : t('start_search')}
                            </h4>
                            <p style={{ fontSize: '14px', color: '#999' }}>
                                {searchMode === 'filtered'
                                    ? t('no_filtered_routes_hint')
                                    : t('start_search_hint')}
                            </p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {displayResults
                                .filter(f => (searchMode === 'filtered' && f.geometry.type !== 'Point') || (searchMode === 'ai'))
                                .map((route, index) => renderRoute(route, index))}
                        </div>
                    )}
                </div>
            </div>

            {/* MODAL METEO */}
            {showWeatherModal && selectedTrailForWeather && (
                <div
                    className="weather-modal-overlay"
                    onClick={() => setShowWeatherModal(false)}
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex', justifyContent: 'center', alignItems: 'center',
                        zIndex: 10000, padding: '20px', animation: 'fadeIn 0.2s ease-out'
                    }}
                >
                    <div
                        className="weather-modal-content"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: 'white', borderRadius: '16px',
                            maxWidth: '800px', width: '100%', maxHeight: '90vh',
                            overflow: 'hidden', display: 'flex', flexDirection: 'column',
                            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
                            animation: 'slideUp 0.3s ease-out'
                        }}
                    >
                        {/* Header Modal */}
                        <div style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '20px 24px', borderBottom: '2px solid #f0f0f0',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white'
                        }}>
                            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                                {selectedTrailForWeather.properties?.name || t('unnamed_route')}
                            </h2>
                            <button
                                onClick={() => setShowWeatherModal(false)}
                                style={{
                                    background: 'rgba(255, 255, 255, 0.2)', border: 'none',
                                    color: 'white', fontSize: '28px', fontWeight: 'bold',
                                    cursor: 'pointer', padding: '4px 12px', borderRadius: '8px',
                                    lineHeight: 1, transition: 'all 0.2s', minWidth: '40px', minHeight: '40px'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                                    e.currentTarget.style.transform = 'rotate(90deg)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                                    e.currentTarget.style.transform = 'rotate(0)';
                                }}
                            >
                                ✕
                            </button>
                        </div>

                        {/* Body Modal con WeatherAlerts */}
                        <div style={{ overflowY: 'auto', padding: '20px 24px', background: '#f9f9f9' }}>
                            <WeatherAlerts
                                coordinates={selectedTrailForWeather.geometry?.coordinates}
                                trailName={selectedTrailForWeather.properties?.name}
                                trail={selectedTrailForWeather}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* KEYFRAMES CSS */}
            <style>{`
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(30px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .weather-modal-content::-webkit-scrollbar { width: 8px; }
                .weather-modal-content::-webkit-scrollbar-track { background: #f1f1f1; }
                .weather-modal-content::-webkit-scrollbar-thumb { background: #888; border-radius: 4px; }
                .weather-modal-content::-webkit-scrollbar-thumb:hover { background: #555; }
                @media (max-width: 768px) {
                    .weather-modal-overlay { padding: 0; align-items: flex-end; }
                    .weather-modal-content { max-width: 100%; max-height: 95vh; border-radius: 20px 20px 0 0; }
                }
            `}</style>
        </>
    );
}
